import { Phone, Clock, MapPin, Menu, X } from "lucide-react";
import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { BUSINESS } from "@/data/business";

const NAV = [
  { label: "Services", hash: "#services" },
  { label: "Why Us", hash: "#why-us" },
  { label: "Service Areas", hash: "#areas" },
  { label: "Reviews", hash: "#reviews" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleAnchor = (e, hash) => {
    e.preventDefault();
    setOpen(false);
    if (location.pathname === "/") {
      document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/");
      setTimeout(() => document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" }), 350);
    }
  };

  return (
    <header className="sticky top-0 z-50" data-testid="site-header">
      {/* Emergency utility bar */}
      <div className="hidden md:flex items-center justify-between bg-slate-950 text-slate-300 text-xs px-6 py-2 border-b border-white/10">
        <div className="flex items-center gap-6">
          <span className="flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5 text-red-600" /> {BUSINESS.hours}
          </span>
          <span className="flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5 text-red-600" /> {BUSINESS.address}
          </span>
        </div>
        <span className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 bg-red-600 text-white px-2.5 py-0.5 font-bold uppercase tracking-wider text-[10px] rounded-sm pulse-ring">
            <span className="h-1.5 w-1.5 rounded-full bg-white" /> 24/7 Emergency
          </span>
        </span>
      </div>

      {/* Main nav */}
      <div className={`bg-slate-900 text-white transition-shadow ${scrolled ? "shadow-lg shadow-black/30" : ""}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <span className="bg-white rounded-sm px-3 py-1.5 inline-flex items-center">
              <img src="/logo.png" alt="Prosper Garage Door Repair" width="185" height="40" className="h-8 sm:h-10 w-auto" />
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((item) => (
              <a
                key={item.hash}
                href={item.hash}
                onClick={(e) => handleAnchor(e, item.hash)}
                className="text-sm font-semibold text-slate-200 hover:text-red-500 transition-colors cursor-pointer"
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {item.label}
              </a>
            ))}
            <Link to="/faq" className="text-sm font-semibold text-slate-200 hover:text-red-500 transition-colors" data-testid="nav-faq">
              FAQ
            </Link>
            <Link to="/garage-door-tips" className="text-sm font-semibold text-slate-200 hover:text-red-500 transition-colors" data-testid="nav-blog">
              Garage Door Tips
            </Link>
            <Link to="/review" className="text-sm font-semibold text-slate-200 hover:text-red-500 transition-colors" data-testid="nav-leave-review">
              Leave a Review
            </Link>
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={`tel:${BUSINESS.phoneRaw}`}
              className="hidden sm:inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 active:scale-[0.98] text-white font-bold text-base px-5 py-2.5 rounded-sm transition-colors"
              data-testid="header-call-button"
            >
              <Phone className="h-4 w-4" />
              {BUSINESS.phoneDisplay}
            </a>
            <button
              className="lg:hidden p-2 text-white"
              onClick={() => setOpen((v) => !v)}
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="lg:hidden border-t border-white/10 bg-slate-900" data-testid="mobile-menu">
            <nav className="px-4 py-4 flex flex-col gap-1">
              {NAV.map((item) => (
                <a
                  key={item.hash}
                  href={item.hash}
                  onClick={(e) => handleAnchor(e, item.hash)}
                  className="py-3 px-2 text-base font-semibold text-slate-200 hover:text-red-500 border-b border-white/5"
                >
                  {item.label}
                </a>
              ))}
              <Link to="/faq" onClick={() => setOpen(false)} className="py-3 px-2 text-base font-semibold text-slate-200 hover:text-red-500 border-b border-white/5">
                FAQ
              </Link>
              <Link to="/garage-door-tips" onClick={() => setOpen(false)} className="py-3 px-2 text-base font-semibold text-slate-200 hover:text-red-500 border-b border-white/5">
                Garage Door Tips
              </Link>
              <Link to="/review" onClick={() => setOpen(false)} className="py-3 px-2 text-base font-semibold text-slate-200 hover:text-red-500 border-b border-white/5">
                Leave a Review
              </Link>
              <a
                href={`tel:${BUSINESS.phoneRaw}`}
                className="mt-3 inline-flex items-center justify-center gap-2 bg-red-600 text-white font-bold text-base px-5 py-3 rounded-sm"
                data-testid="mobile-call-button"
              >
                <Phone className="h-4 w-4" /> {BUSINESS.phoneDisplay}
              </a>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
